import { useState } from "react";
import { Typography, TextField, FormControlLabel, Radio, RadioGroup, Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CouponTemplateFormDto, CodeType } from "../../../store/models/coupon/CouponManagementDto";

interface CouponCodeGenerationStepProps {
  formData: CouponTemplateFormDto;
  updateFormData: (updates: Partial<CouponTemplateFormDto>) => void;
  readOnly?: boolean;
}

const CouponCodeGenerationStep = ({ formData, updateFormData, readOnly = false }: CouponCodeGenerationStepProps) => {
  const { t } = useTranslation();
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  const handleFieldTouch = (fieldName: string) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleCodeTypeChange = (newType: CodeType) => {
    updateFormData({
      codeType: newType,
      // Clear fields based on code type
      couponCode: newType === CodeType.GENERIC ? formData.couponCode : undefined,
      codePrefix: newType === CodeType.INDIVIDUAL_GENERATED ? formData.codePrefix : undefined,
      codeLength: newType === CodeType.INDIVIDUAL_GENERATED ? formData.codeLength : undefined,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#3859ff" }}>
        {t("coupon.form.codeGeneration")}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Code Type Selection */}
        <Grid item xs={12}>
          <RadioGroup value={formData.codeType} onChange={(e) => handleCodeTypeChange(e.target.value as CodeType)}>
            <FormControlLabel
              value={CodeType.GENERIC}
              control={<Radio sx={{ color: "#3859ff" }} disabled={readOnly} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("coupon.form.codeType.generic")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("coupon.form.codeType.genericDescription")}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              value={CodeType.INDIVIDUAL_GENERATED}
              control={<Radio sx={{ color: "#3859ff" }} disabled={readOnly} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("coupon.form.codeType.individual")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("coupon.form.codeType.individualDescription")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Grid>

        {/* Dynamic Code Input */}
        <Grid item xs={12}>
          <Box sx={{ pt: 2, borderTop: "1px solid #e5e7eb" }}>
            {formData.codeType === CodeType.GENERIC ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("coupon.form.genericCode")}
                    value={formData.couponCode || ""}
                    onChange={(e) => updateFormData({ couponCode: e.target.value || undefined })}
                    onBlur={() => handleFieldTouch("couponCode")}
                    placeholder="e.g., WELCOME10"
                    sx={{ backgroundColor: "white" }}
                    error={touchedFields.couponCode && !formData.couponCode}
                    helperText={
                      touchedFields.couponCode && !formData.couponCode
                        ? t("coupon.form.errors.genericCodeRequired")
                        : ""
                    }
                    required
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("coupon.form.codePrefix")}
                    value={formData.codePrefix || ""}
                    onChange={(e) => updateFormData({ codePrefix: e.target.value || undefined })}
                    onBlur={() => handleFieldTouch("codePrefix")}
                    placeholder="e.g., SAVE"
                    sx={{ backgroundColor: "white" }}
                    helperText="Optional prefix for generated codes"
                    disabled={readOnly}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t("coupon.form.codeLength")}
                    value={formData.codeLength || ""}
                    onChange={(e) => updateFormData({ codeLength: Number.parseInt(e.target.value) || undefined })}
                    onBlur={() => handleFieldTouch("codeLength")}
                    placeholder="e.g., 8"
                    sx={{ backgroundColor: "white" }}
                    error={touchedFields.codeLength && (!formData.codeLength || formData.codeLength <= 0)}
                    helperText={
                      touchedFields.codeLength && (!formData.codeLength || formData.codeLength <= 0)
                        ? t("coupon.form.errors.codeLengthRequired")
                        : "Length of the random code (e.g., 8 characters)"
                    }
                    inputProps={{ min: 1 }}
                    required
                    disabled={readOnly}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CouponCodeGenerationStep;
